import React from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refresh } = useAuth();
  const [state, setState] = React.useState("verifying"); // verifying | success | error
  const [message, setMessage] = React.useState("");

  usePageMeta({
    title: "Confirm your email | CV AI PRO",
    description: "Confirm your email address.",
    path: "/verify-email",
  });

  React.useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await axios.post(
          `${API}/auth/verify-email`,
          { token },
          { withCredentials: true }
        );
        if (!cancelled) {
          setState("success");
          refresh(); // pull email_verified=true into the session
        }
      } catch (err) {
        if (!cancelled) {
          setState("error");
          const detail = err?.response?.data?.detail;
          const msg = Array.isArray(detail)
            ? "This verification link is invalid. Request a new one."
            : typeof detail === "string"
              ? detail
              : "Verification failed. Request a new link.";
          setMessage(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="verify-email-page">
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/15 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg" />

      <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 pb-24 text-center">
        {state === "verifying" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="verify-pending">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-zinc-400" />
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
              Confirming your email…
            </h1>
          </motion.div>
        )}

        {state === "success" && (
          <>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30"
            >
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mt-8 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white text-balance"
              data-testid="verify-success-headline"
            >
              Email confirmed.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.6 }}
              className="mt-5 max-w-xl mx-auto text-zinc-400 text-balance"
            >
              Your account is fully active — the AI studio is all yours.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center"
            >
              <Link
                to="/upload"
                className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors"
                data-testid="verify-success-cta"
              >
                <Sparkles className="h-4 w-4" />
                Open the AI studio
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/account"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
                data-testid="verify-success-account"
              >
                My account
              </Link>
            </motion.div>
          </>
        )}

        {state === "error" && (
          <>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 border border-red-500/30">
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
            <h1
              className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance"
              data-testid="verify-error-headline"
            >
              This link didn't work.
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-balance" data-testid="verify-error-message">
              {message} Log in and use the "Resend" button on your account page to get a fresh link.
            </p>
            <div className="mt-10">
              <Link
                to="/login?next=/account"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
                data-testid="verify-error-login"
              >
                Go to login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
